'use strict';

/**
 * @param {String} type
 * @return {Function|ReduxActionCreator}
 */
export function makeActionCreator(type) {
  const action = function (payload={}, meta) {
    return {
      type,
      payload,
      meta,
    };
  };

  action.TYPE = type;

  return action;
}

/**
 * @typedef {Object} FilterData
 * @property {String} filter
 * @property {?Object} [filterOptions]
 * @property {?Object} [sortOptions]
 */

/**
 * @param {ReduxObservableListOptions} options
 * @return {ReduxObservableListActions}
 */
export function buildListActions(options) {
  const actions = {};

  // List actions
  const INIT_LIST = `${options.prefix}/INIT_LIST`;
  const HYDRATE_LIST = `${options.prefix}/HYDRATE_LIST`;
  const LOAD_SUCCESS = `${options.prefix}/LOAD_SUCCESS`;
  const LOAD_PAGE = `${options.prefix}/LOAD_PAGE`;
  const LOAD_PAGE_SUCCESS = `${options.prefix}/LOAD_PAGE_SUCCESS`;
  const LOAD_PAGE_ERROR = `${options.prefix}/LOAD_PAGE_ERROR`;
  const SYNC_LIST_SUCCESS = `${options.prefix}/SYNC_LIST_SUCCESS`;
  const AUTO_FILTER = `${options.prefix}/AUTO_FILTER`;
  const DESTROY_LIST = `${options.prefix}/DESTROY_LIST`;

  // List-affecting CRUD actions
  const DELETE_ITEM = `${options.prefix}/DELETE_ITEM`;
  const DELETE_ITEM_SUCCESS = `${options.prefix}/DELETE_ITEM_SUCCESS`;
  const DELETE_ITEM_ERROR = `${options.prefix}/DELETE_ITEM_ERROR`;

  // Sync Model Actions
  const UPDATE_SYNC_STATUS = `${options.prefix}/UPDATE_SYNC_STATUS`;

  /**
   * Store the parent ID the action is targeting.
   * @typedef {Object} ReduxObservableActionMeta
   * @property {String} [parentId]
   */

  /**
   * The initLiast action may optionally start the list in a filtered state.
   * @typedef {Object} InitListPayload
   * @property {String} [parentId]
   * @property {String} [filter]
   * @property {?Object} [filterOptions]
   * @property {?Object} [sortOptions]
   */

  /**
   * @param {InitListPayload} payload
   * @return {ReduxAction<InitListPayload>}
   */
  actions.initList = makeActionCreator(INIT_LIST);

  /**
   * @param {ReduxObservableListState} payload
   * @return {ReduxAction<ReduxObservableListState>}
   */
  actions.hydrateList = makeActionCreator(HYDRATE_LIST);

  /**
   * Contains a map of updated list elements with their DB ID as the keys.
   * @typedef {Object} LoadSuccessPayload
   * @property {Object<String, Object>} list
   */

  /**
   *
   * @param {LoadSuccessPayload} payload
   * @param {ReduxObservableActionMeta} meta
   * @return {ReduxAction<LoadSuccessPayload>}
   */
  actions.loadSuccess = makeActionCreator(LOAD_SUCCESS);

  /**
   * TODO: we should probably just get this from the state like the page number...
   * @param {FilterData} payload - The currently applied filter
   * @param {ReduxObservableActionMeta} meta
   * @return {ReduxAction<FilterData>}
   */
  actions.loadPage = makeActionCreator(LOAD_PAGE);

  /**
   * @typedef {Object} LoadPageSuccessPayload
   * @property {Boolean} hasMore Whether the remote store has more items available to load
   * @property {Number} currentPage The page number that was requested successfully
   * @property {Array<Object>} page The raw page data
   */

  /**
   * @param {LoadPageSuccessPayload} payload
   * @return {ReduxAction<LoadPageSuccessPayload>}
   */
  actions.loadPageSuccess = makeActionCreator(LOAD_PAGE_SUCCESS);

  /**
   * @typedef {Object} LoadPageError
   * @property {Error} err
   * @property {ViewMode} mode
   */

  /**
   * @param {LoadPageError} payload
   * @return {ReduxAction<LoadPageError>}
   */
  actions.loadPageError = makeActionCreator(LOAD_PAGE_ERROR);

  /**
   * @typedef {LoadSuccessPayload} SyncListSuccessPayload
   * @property {Boolean} hasMore Whether the remote store has more items available to load
   * @property {Number} currentPage The page number at the time of request
   * @property {Boolean} isFiltered The filter status at the time of request
   */

  /**
   * @param {SyncListSuccessPayload} payload
   * @return {ReduxAction<SyncListSuccessPayload>}
   */
  actions.syncListSuccess = makeActionCreator(SYNC_LIST_SUCCESS);

  /**
   * @param {FilterData} [payload={}]
   * @return {ReduxAction<FilterData>}
   */
  actions.applyFilter = makeActionCreator(AUTO_FILTER);

  /**
   * @return {ReduxAction<Object>}
   */
  actions.destroyList = makeActionCreator(DESTROY_LIST);

  /**
   * @typedef {Object} DeleteItemPayload
   * @property {String} id
   */

  /**
   * @param {DeleteItemPayload} payload
   * @return {ReduxAction<DeleteItemPayload>}
   */
  actions.deleteItem = makeActionCreator(DELETE_ITEM);

  /**
   * @typedef {Object} DeleteItemSuccessPayload
   * @property {String} id
   */

  /**
   * @param {DeleteItemSuccessPayload} payload
   * @return {ReduxAction<DeleteItemSuccessPayload>}
   */
  actions.deleteItemSuccess = makeActionCreator(DELETE_ITEM_SUCCESS);

  /**
   * @typedef {Object} DeleteItemErrorPayload
   * @property {String} id
   * @property {Error} err
   */

  /**
   * @param {DeleteItemErrorPayload} payload
   * @return {ReduxAction<DeleteItemErrorPayload>}
   */
  actions.deleteItemError = makeActionCreator(DELETE_ITEM_ERROR);

  /**
   * @typedef {Object} UpdateSyncStatusPayload
   * @property {String} id
   * @property {String} [requestId] only applied if options.canCreate == true
   * @property {SyncStatus} syncStatus
   * @property {Number} syncTime Unix timestamp at the time of dispatching the attempt to sync.
   * @property {Object} [model]
   */

  /**
   * @param {UpdateSyncStatusPayload} payload
   * @return {ReduxAction<UpdateSyncStatusPayload>}
   */
  actions.updateSyncStatus = makeActionCreator(UPDATE_SYNC_STATUS);

  return actions;
}

/**
 * @typedef {Object} ReduxObservableListActions
 * @property {Function|ReduxActionCreator<InitListPayload>} initList
 * @property {Function|ReduxActionCreator<ReduxObservableListState>} hydrateList
 * @property {Function|ReduxActionCreator<LoadSuccessPayload>} loadSuccess
 * @property {Function|ReduxActionCreator<FilterData>} loadPage
 * @property {Function|ReduxActionCreator<LoadPageSuccessPayload>} loadPageSuccess
 * @property {Function|ReduxActionCreator<LoadPageError>} loadPageError
 * @property {Function|ReduxActionCreator<SyncListSuccessPayload>} syncListSuccess
 * @property {Function|ReduxActionCreator<FilterData>} applyFilter
 * @property {Function|ReduxActionCreator<Void>} destroyList
 *
 * @property {Function|ReduxActionCreator<DeleteItemPayload>} deleteItem
 * @property {Function|ReduxActionCreator<DeleteItemSuccessPayload>} deleteItemSuccess
 * @property {Function|ReduxActionCreator<DeleteItemErrorPayload>} deleteItemError
 *
 * @property {Function|ReduxActionCreator<UpdateSyncStatusPayload>} updateSyncStatus
 */



// WEBPACK FOOTER //
// ./src/js/_libs/redux-observable-list/src/actions.js